'use client'

import { UserApiResponse } from '@interfaces/userApiResponse'
import { checkIsLoggedIn } from '@utils/client/isLoggedIn'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'

interface UserContext {
  isLoggedIn: boolean | undefined
  setUserLoggedIn: (item: boolean) => void
  userData: UserApiResponse | undefined
}

const UserContextC = createContext<UserContext | undefined>(undefined)

interface UserProviderProps {
  children: ReactNode
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [isLoggedIn, setUserLoggedIn] = useState<boolean | undefined>(undefined)
  const [userData, setUserData] = useState<UserApiResponse | undefined>(undefined)

  const contextValue: UserContext = useMemo(
    () => ({
      isLoggedIn,
      setUserLoggedIn,
      userData,
    }),
    [isLoggedIn, userData]
  )

  useEffect(() => {
    void (async () => {
      const res = await checkIsLoggedIn()
      if (res.isLoggedIn === isLoggedIn) return
      setUserLoggedIn(res.isLoggedIn)
      if (res.user) setUserData(res.user)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <UserContextC.Provider value={contextValue}>{children}</UserContextC.Provider>
}

export const useUserContext = () => {
  const context = useContext(UserContextC)
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}
